import { adminGetVotings } from '../../../features/queries/adminGetVotings';
import { shortId } from '../../../lib/ids';
import { useInfiniteQ, useLoadPagesThrough } from '../../../lib/query';
import { NEW_VOTING_PATH, adminVotingStatus } from '../../../lib/votings';
import { useLayout } from '../../../providers/LayoutProvider';
import { useResponsive } from '../../../providers/ResponsiveProvider';
import { __, toastError } from '../../../utils';
import AddIcon from '@mui/icons-material/Add';
import {
  Box,
  DataTable,
  EmptyState,
  Pagination,
  StatusChip,
  useUrlFilters,
  type Column,
} from '@presov.sk/presov-ui-react';
import { formatDate } from 'date-fns';
import React from 'react';
import { useNavigate, useSearchParams } from 'react-router';

const LIMIT = 15;

type Voting = {
  voting_id: string;
  title: string;
  inserted_at: string;
  open_from: Date | null;
  open_to: Date | null;
  forgotten?: boolean;
  verified_accounts_only?: boolean;
  allow_anonymous?: boolean;
};

const dateTime = (value: Date | string | null | undefined) =>
  value ? formatDate(new Date(value), 'dd.MM.yyyy HH:mm') : null;

/**
 * Správa hlasovaní: tabuľka na plátne a `Pagination` v pripnutej pätičke.
 *
 * Lišta filtrov tu **nie je** zámerne - `adminGetVotings` pozná len `limit`
 * a `pagecursor`, takže hľadanie by vedelo prehľadať iba načítanú stranu
 * a tvárilo by sa, že prehľadalo zoznam. Keď API dostane `q`, pridá sa
 * `FilterBar` rovnako ako v správe podnetov.
 */
const VotingAdminPage: React.FC = () => {
  const { isMd } = useResponsive();
  const navigate = useNavigate();
  const { setActions, setFooter } = useLayout();

  const now = React.useMemo(() => new Date(), []);

  // Strana patrí do URL - odkaz na tretiu stranu zoznamu má na tretiu stranu
  // aj viesť. Filtre ani hľadanie tu nie sú, `adminGetVotings` ich nepozná.
  const [searchParams, setSearchParams] = useSearchParams();
  const { page: pageIndex, setPage: setPageIndex } = useUrlFilters({
    searchParams,
    setSearchParams,
  });

  const {
    data,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
    isLoading,
    isError,
  } = useInfiniteQ(
    adminGetVotings,
    { limit: LIMIT },
    {
      initialPageParam: undefined,
      getNextPageParam: (lastPage) => {
        if (lastPage.length < LIMIT) {
          return undefined;
        }
        const lastItem = lastPage[lastPage.length - 1];
        return [lastItem.open_from, lastItem.voting_id];
      },
    },
  );

  const pages = React.useMemo(
    () =>
      data?.pages.map((page) =>
        page.map(
          (item): Voting => ({
            ...item,
            open_from: item.open_from ? new Date(item.open_from) : null,
            open_to: item.open_to ? new Date(item.open_to) : null,
          }),
        ),
      ) ?? [],
    [data],
  );

  // F5 na `?page=2`: nekonečný dotaz začína od prvej strany, takže sa musí
  // dočítať až po tú z adresy (`useLoadPagesThrough`).
  useLoadPagesThrough({
    pageIndex,
    loadedPages: data?.pages.length ?? 0,
    hasNextPage: !!hasNextPage,
    isFetching: isLoading || isFetchingNextPage,
    fetchNextPage,
    setPageIndex,
  });

  const votings = pages[pageIndex] ?? [];

  React.useEffect(() => {
    if (isError) {
      toastError(__('Hlasovania sa nepodarilo načítať.'));
    }
  }, [isError]);

  React.useEffect(() => {
    setActions([
      {
        label: __('Vytvoriť hlasovanie'),
        onClick: () => navigate(NEW_VOTING_PATH),
        variant: 'dark',
        icon: <AddIcon />,
      },
    ]);
  }, [navigate, setActions]);

  const columns: Column<Voting>[] = React.useMemo(
    () => [
      {
        key: 'voting_id',
        label: __('ID'),
        // Identifikátor má **vždy** 7 znakov (`platform.identifier_length`
        // v `digiobcan-api`) a predpona sa v bunke nekreslí, takže stĺpec je
        // pevný, nie pružný - `fr` mu bralo šírku podľa okna a zvyšok riadku
        // o ňu prichádzal. Platí to odteraz aj pre kategórie: migrácia 000018
        // prepísala ich čitateľné kľúče na generované.
        width: '92px',
        minWidth: '92px',
        mono: true,
        render: (voting) => shortId(voting.voting_id),
      },
      {
        key: 'title',
        label: __('Názov'),
        width: '2.4fr',
        minWidth: '220px',
        strong: true,
      },
      {
        key: 'inserted_at',
        label: __('Vytvorené'),
        width: '1.1fr',
        minWidth: '160px',
        render: (voting) => dateTime(voting.inserted_at),
      },
      {
        key: 'open_from',
        label: __('Otvorené od'),
        width: '1.1fr',
        minWidth: '160px',
        render: (voting) => dateTime(voting.open_from),
      },
      {
        key: 'open_to',
        label: __('Otvorené do'),
        width: '1.1fr',
        minWidth: '160px',
        render: (voting) => dateTime(voting.open_to),
      },
      {
        key: 'status',
        label: __('Stav'),
        width: '1fr',
        minWidth: '140px',
        render: (voting) => {
          const { label, tone } = adminVotingStatus(voting, now);
          return <StatusChip tone={tone}>{label}</StatusChip>;
        },
      },
      {
        // Na desktope to doteraz nebolo vidno vôbec, na mobile to karta mala.
        // Keďže karty vykresľujú stĺpce tabuľky, buď je to stĺpec, alebo to
        // z mobilu zmizne - a „kto smie hlasovať" je informácia pre správcu.
        key: 'verified_accounts_only',
        label: __('Prístup'),
        width: '0.9fr',
        minWidth: '120px',
        render: (voting) =>
          voting.allow_anonymous ? (
            <StatusChip tone='neutral'>{__('Aj neprihlásení')}</StatusChip>
          ) : voting.verified_accounts_only ? (
            <StatusChip tone='warning'>{__('Iba overení')}</StatusChip>
          ) : null,
      },
    ],
    [now],
  );

  // Riadok je odkaz (`<a href>` z `rowHref`), aby sa hlasovanie dalo otvoriť
  // v novej karte; routuje `LinkProvider` inštalovaný shellom.
  const rowHref = React.useCallback(
    (voting: Voting) =>
      `/administracia/hlasovania/${shortId(voting.voting_id)}`,
    [],
  );

  const hasNext =
    (pageIndex < pages.length - 1 || hasNextPage) && !isFetchingNextPage;

  React.useEffect(() => {
    setFooter(
      <Pagination
        page={pageIndex}
        hasNext={hasNext}
        onPageChange={(next) => {
          if (next >= pages.length && hasNextPage) {
            fetchNextPage();
          }
          setPageIndex(next);
        }}
      />,
    );

    return () => setFooter(null);
  }, [
    setFooter,
    setPageIndex,
    pageIndex,
    hasNext,
    hasNextPage,
    fetchNextPage,
    pages.length,
  ]);

  return (
    <Box sx={{ padding: 1.5 }}>
      <DataTable
        variant={isMd ? 'table' : 'cards'}
        columns={columns}
        rows={votings}
        rowKey='voting_id'
        rowHref={rowHref}
        loading={isLoading || isFetchingNextPage}
        empty={
          <EmptyState
            compact
            icon='list'
            title={__('Žiadne hlasovania')}
            description={__('Nové hlasovanie vytvoríš tlačidlom v hlavičke.')}
          />
        }
      />
    </Box>
  );
};

export default VotingAdminPage;
